import { apiFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || '';

export const OPEN_CODESYS_AGENT_SETTINGS_EVENT = 'realplc:open-codesys-agent-settings';

export function requestOpenCodesysAgentSettings(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_CODESYS_AGENT_SETTINGS_EVENT));
  }
}

export interface CodesysCapabilitiesResponse {
  connected: boolean;
  agentId: string | null;
  connector?: Record<string, unknown> | null;
  codesys: Record<string, unknown> | null;
}

export interface CodesysValidationRunSummary {
  id: string;
  engine: 'codesys';
  status: string;
  codeArtifactId: string;
  testSpecArtifactId?: string | null;
  currentAttempt?: number;
  requiredLevel?: string | null;
  achievedLevel?: string | null;
  failureReason?: string | null;
  finalSummary?: Record<string, unknown> | null;
  createdAt?: number;
  updatedAt?: number;
}

export interface CodesysValidationAttemptSummary {
  id: string;
  attemptNo: number;
  status: string;
  achievedLevel?: string | null;
  failureClass?: string | null;
  diagnosticsSummary?: Record<string, unknown> | null;
  diagnosticsData?: Record<string, unknown> | null;
}

export interface CodesysValidationJobSummary {
  id: string;
  status: string;
  failureCode?: string | null;
  errorMessage?: string | null;
  achievedLevel?: string | null;
}

export interface CodesysValidationResultSummary {
  resultId: string;
  accepted: boolean;
  normalizedDiagnostics?: Record<string, unknown> | null;
}

export interface CodesysValidationRunDetail {
  run: CodesysValidationRunSummary;
  attempts: CodesysValidationAttemptSummary[];
  jobs: CodesysValidationJobSummary[];
  results: CodesysValidationResultSummary[];
}

export interface CodesysValidationRunStart {
  runId: string;
  attemptId?: string;
  jobId?: string;
  status: string;
  requiredLevel?: string;
  dispatched?: boolean;
  detail?: CodesysValidationRunDetail;
}

export class CodesysAgentApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly code?: string,
    /** Seconds until the gateway will accept the request again, when it said so. */
    public readonly retryAfterSec?: number,
  ) {
    super(message);
    this.name = 'CodesysAgentApiError';
  }
}

/**
 * What the gateway means when it refuses to make a CODESYS artifact out of the
 * delivered text.
 *
 * 2026-09-21 线上：交付的文本从正文语句开始——没有 PROGRAM 头、没有声明——网关把
 * 第一个对象关键字之前的一切静默丢掉，只把剩下的一个 VAR_GLOBAL CONSTANT 块送去
 * 编译，CODESYS 报「0 errors」，界面告诉工程师「CODESYS 原生编译通过」。网关现在
 * 拒收这种输入，也拒收只有变量表 / 类型定义、没有一个 POU 的产物（同一种空洞的
 * 结论），而拒收的理由要以工程师读得懂的方式到屏幕上，不能只落一个错误码。
 *
 * 两条路都用它：浏览器自己发起的 Run（错误消息就是这个码），以及 worker 记下的
 * `Gateway 400: {"error":…,"message":…}`（码在 JSON 里）。所以按包含匹配。
 */
export function describeCodesysArtifactError(message: string | undefined, zh: boolean): string | null {
  if (!message) return null;
  if (message.includes('CODESYS_RAW_ST_ORPHAN_SOURCE')) {
    return zh
      ? '这份程序在第一个 PROGRAM/FUNCTION_BLOCK 之前、或在所有对象之外还有语句，不是一份完整的程序，没有送去编译。'
      : 'This program has statements before its first PROGRAM/FUNCTION_BLOCK, or outside any object; it is not a complete program, and was not sent to the compiler.';
  }
  if (message.includes('CODESYS_RAW_ST_NO_POU')) {
    return zh
      ? '这份程序里没有 PROGRAM、FUNCTION_BLOCK 或 FUNCTION，只有变量表或类型定义，没有可编译的程序，没有送去编译。'
      : 'This program has no PROGRAM, FUNCTION_BLOCK or FUNCTION, only variable lists or type definitions; there is nothing to compile, and it was not sent to the compiler.';
  }
  return null;
}

/**
 * What a failed CODESYS Agent request means to the reader, in their language.
 *
 * The gateway answers a rate limit with «Too many API requests, please try
 * again later» and a `retryAfter`; the step row used to print that English
 * sentence as its body, under a Chinese heading, with nothing about what to do
 * (production, 2026-09-15). The limit is the organisation's minute-quota on the
 * gateway, not anything about the program — the row has to say so, and say
 * when it can be retried.
 */
export function describeCodesysAgentError(error: unknown, zh: boolean): string {
  if (error instanceof CodesysAgentApiError && (error.statusCode === 429 || error.code === 'RATE_LIMIT_EXCEEDED')) {
    const wait = error.retryAfterSec && error.retryAfterSec > 0
      ? (zh ? `约 ${error.retryAfterSec} 秒后可以重试。` : `It can be retried in about ${error.retryAfterSec} seconds.`)
      : (zh ? '稍后可以重试。' : 'It can be retried shortly.');
    return zh
      ? `服务器这一分钟收到的请求过多，暂时拒绝了这一步；这不是程序的问题。${wait}`
      : `The server refused this step because it received too many requests this minute; the program itself is fine. ${wait}`;
  }
  if (error instanceof Error) {
    const artifact = describeCodesysArtifactError(error.message, zh);
    if (artifact) return artifact;
    if (error.message.trim()) return error.message;
  }
  return zh ? '启动 CODESYS 验证失败。' : 'Failed to start CODESYS validation.';
}

export async function loadCodesysCapabilities(projectId: string): Promise<CodesysCapabilitiesResponse> {
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/codesys/capabilities`,
  );
  if (!response.ok) throw await readError(response, 'Failed to load CODESYS Agent capabilities');
  return response.json() as Promise<CodesysCapabilitiesResponse>;
}

export async function createCodesysValidationRun(input: {
  projectId: string;
  codeArtifactId: string;
  testSpecArtifactId?: string;
  requirementArtifactId?: string;
  agentId?: string;
  clientRequestId: string;
  /**
   * The deliverable stem, e.g. `ConveyorControl_20260921_V1.0`. The Agent names
   * the `.project` it opens on the desktop after it; without it, it falls back
   * to `<first object>_v<version>`, which on 2026-09-21 was
   * `V1_0_Constants_v1.project` beside a zip with another name.
   */
  projectName?: string;
}): Promise<CodesysValidationRunStart> {
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(input.projectId)}/codesys/validation-runs`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': input.clientRequestId,
      },
      body: JSON.stringify({
        codeArtifactId: input.codeArtifactId,
        testSpecArtifactId: input.testSpecArtifactId,
        requirementArtifactId: input.requirementArtifactId,
        agentId: input.agentId,
        clientRequestId: input.clientRequestId,
        projectName: input.projectName,
        source: { kind: 'standard-template' },
        scope: 'pou',
        requiredLevel: input.testSpecArtifactId ? 'L3_SIMULATION' : 'L2_INDUSTRIAL_RULES',
        runtimeBackend: input.testSpecArtifactId ? 'ide-simulation' : 'compile-only',
      }),
    },
  );
  if (!response.ok) throw await readError(response, 'Failed to create CODESYS validation run');

  const data = await response.json() as Partial<CodesysValidationRunStart> & Partial<CodesysValidationRunDetail>;
  const runId = typeof data.runId === 'string'
    ? data.runId
    : typeof data.run?.id === 'string' ? data.run.id : '';
  if (!runId) throw new Error('CODESYS validation response did not include a Run ID.');
  return {
    runId,
    attemptId: data.attemptId,
    jobId: data.jobId,
    status: typeof data.status === 'string' ? data.status : data.run?.status ?? 'running',
    requiredLevel: data.requiredLevel ?? data.run?.requiredLevel ?? undefined,
    dispatched: data.dispatched,
    ...(data.run ? { detail: data as CodesysValidationRunDetail } : {}),
  };
}

export async function loadCodesysValidationRun(
  projectId: string,
  runId: string,
): Promise<CodesysValidationRunDetail> {
  const response = await apiFetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/codesys/validation-runs/${encodeURIComponent(runId)}`,
  );
  if (!response.ok) throw await readError(response, 'Failed to load CODESYS validation run');
  return response.json() as Promise<CodesysValidationRunDetail>;
}

async function readError(response: Response, fallback: string): Promise<CodesysAgentApiError> {
  const data = await response.json().catch(() => null) as { message?: string; error?: string; retryAfter?: number } | null;
  return new CodesysAgentApiError(
    data?.message || fallback,
    response.status,
    data?.error,
    typeof data?.retryAfter === 'number' && Number.isFinite(data.retryAfter) ? data.retryAfter : undefined,
  );
}
